// Servicio de Alertas Configurables
// Gestiona las alertas del sistema y sus configuraciones

import { getDocument, getCollection, createDocument, updateDocument } from './firestore.service'
import { Timestamp } from 'firebase/firestore'

const ALERTS_SETTINGS_COLLECTION = 'alertsSettings'
const PURCHASE_ORDERS_COLLECTION = 'purchaseOrders'

/**
 * Obtener configuración de alertas
 * @returns {Promise<Object>} Configuración de alertas
 */
export const getAlertsSettings = async () => {
  try {
    try {
      const settings = await getDocument(ALERTS_SETTINGS_COLLECTION, 'main')
      if (settings) {
        return settings
      }
    } catch (error) {
      // Si no existe, continuar para crear uno por defecto
    }
    
    // Crear configuración por defecto
    const defaultSettings = {
      id: 'main',
      purchaseOrders: {
        enabled: true,
        deliveryDateWarningDays: 3, // Alertar 3 días antes de la fecha de entrega
        overdueDeliveryDays: 0, // Alertar si pasó la fecha de entrega
        pendingPaymentDays: 7, // Alertar si hay pagos pendientes por más de 7 días
        pendingReceiptDays: 7, // Alertar si hay recepciones pendientes por más de 7 días
        pendingConfirmationDays: 2 // Alertar si hay pedidos pendientes de confirmar por más de 2 días
      },
      updatedAt: Timestamp.now()
    }
    
    const docId = await createDocument(ALERTS_SETTINGS_COLLECTION, defaultSettings)
    return { id: docId, ...defaultSettings }
  } catch (error) {
    console.error('Error obteniendo configuración de alertas:', error)
    throw error
  }
}

/**
 * Actualizar configuración de alertas
 * @param {Object} settingsData - Datos de configuración
 * @returns {Promise<void>}
 */
export const updateAlertsSettings = async (settingsData) => {
  try {
    let existing = null
    try {
      existing = await getDocument(ALERTS_SETTINGS_COLLECTION, 'main')
    } catch (error) {
      // Si no existe, continuar para crear uno nuevo
    }
    
    const data = {
      id: 'main',
      purchaseOrders: {
        enabled: settingsData.purchaseOrders?.enabled !== false,
        deliveryDateWarningDays: parseInt(settingsData.purchaseOrders?.deliveryDateWarningDays) || 3,
        overdueDeliveryDays: parseInt(settingsData.purchaseOrders?.overdueDeliveryDays) || 0,
        pendingPaymentDays: parseInt(settingsData.purchaseOrders?.pendingPaymentDays) || 7,
        pendingReceiptDays: parseInt(settingsData.purchaseOrders?.pendingReceiptDays) || 7,
        pendingConfirmationDays: parseInt(settingsData.purchaseOrders?.pendingConfirmationDays) || 2
      },
      updatedAt: Timestamp.now()
    }
    
    if (existing) {
      await updateDocument(ALERTS_SETTINGS_COLLECTION, 'main', data)
    } else {
      await createDocument(ALERTS_SETTINGS_COLLECTION, data)
    }
  } catch (error) {
    console.error('Error actualizando configuración de alertas:', error)
    throw error
  }
}

/**
 * Obtener alertas de pedidos de compra
 * @returns {Promise<Array>} Array de alertas
 */
export const getPurchaseOrderAlerts = async () => {
  try {
    const settings = await getAlertsSettings()
    if (!settings.purchaseOrders?.enabled) {
      return []
    }
    
    const alerts = []
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    
    // Obtener todos los pedidos activos (no completados ni cancelados)
    const allOrders = await getCollection(PURCHASE_ORDERS_COLLECTION, [])
    const activeOrders = allOrders.filter(order => 
      order.status !== 'completed' && order.status !== 'cancelled'
    )
    
    for (const order of activeOrders) {
      // Alerta: Fecha de entrega próxima
      if (order.expectedDeliveryDate) {
        const deliveryDate = order.expectedDeliveryDate.toDate()
        const daysUntilDelivery = Math.ceil((deliveryDate - today) / (1000 * 60 * 60 * 24))
        
        if (daysUntilDelivery <= settings.purchaseOrders.deliveryDateWarningDays && daysUntilDelivery >= 0) {
          alerts.push({
            id: `delivery-warning-${order.id}`,
            type: 'warning',
            urgency: daysUntilDelivery === 0 ? 'high' : daysUntilDelivery === 1 ? 'medium' : 'low',
            title: 'Fecha de entrega próxima',
            message: `El pedido ${order.orderNumber} tiene fecha de entrega en ${daysUntilDelivery} día(s)`,
            orderId: order.id,
            orderNumber: order.orderNumber,
            date: deliveryDate,
            action: `/admin/pedidos-compra/${order.id}`
          })
        }
        
        // Alerta: Fecha de entrega vencida
        if (daysUntilDelivery < 0 && settings.purchaseOrders.overdueDeliveryDays === 0) {
          const daysOverdue = Math.abs(daysUntilDelivery)
          alerts.push({
            id: `delivery-overdue-${order.id}`,
            type: 'error',
            urgency: 'high',
            title: 'Fecha de entrega vencida',
            message: `El pedido ${order.orderNumber} tiene ${daysOverdue} día(s) de retraso`,
            orderId: order.id,
            orderNumber: order.orderNumber,
            date: deliveryDate,
            action: `/admin/pedidos-compra/${order.id}`
          })
        }
      }
      
      // Alerta: Pendiente de confirmación
      if (order.status === 'pending') {
        const createdDate = order.createdAt.toDate()
        const daysPending = Math.ceil((today - createdDate) / (1000 * 60 * 60 * 24))
        
        if (daysPending >= settings.purchaseOrders.pendingConfirmationDays) {
          alerts.push({
            id: `pending-confirmation-${order.id}`,
            type: 'warning',
            urgency: daysPending >= 5 ? 'high' : daysPending >= 3 ? 'medium' : 'low',
            title: 'Pedido pendiente de confirmación',
            message: `El pedido ${order.orderNumber} lleva ${daysPending} día(s) sin confirmar`,
            orderId: order.id,
            orderNumber: order.orderNumber,
            date: createdDate,
            action: `/admin/pedidos-compra/${order.id}`
          })
        }
      }
      
      // Alerta: Pagos pendientes
      if (order.status === 'confirmed' || order.status === 'partially_paid') {
        const confirmedDate = order.confirmedAt?.toDate() || order.createdAt.toDate()
        const daysSinceConfirmed = Math.ceil((today - confirmedDate) / (1000 * 60 * 60 * 24))
        
        if (daysSinceConfirmed >= settings.purchaseOrders.pendingPaymentDays) {
          const pendingAmount = order.total - (order.totalPaid || 0)
          alerts.push({
            id: `pending-payment-${order.id}`,
            type: 'warning',
            urgency: daysSinceConfirmed >= 14 ? 'high' : daysSinceConfirmed >= 10 ? 'medium' : 'low',
            title: 'Pago pendiente',
            message: `El pedido ${order.orderNumber} tiene $${pendingAmount.toLocaleString()} pendiente de pago (${daysSinceConfirmed} días)`,
            orderId: order.id,
            orderNumber: order.orderNumber,
            date: confirmedDate,
            action: `/admin/pedidos-compra/${order.id}`
          })
        }
      }
      
      // Alerta: Recepciones pendientes
      if (order.status === 'paid' || order.status === 'partially_received') {
        const paidDate = order.updatedAt.toDate() // Usar última actualización como aproximación
        const daysSincePaid = Math.ceil((today - paidDate) / (1000 * 60 * 60 * 24))
        
        if (daysSincePaid >= settings.purchaseOrders.pendingReceiptDays) {
          alerts.push({
            id: `pending-receipt-${order.id}`,
            type: 'info',
            urgency: daysSincePaid >= 14 ? 'high' : daysSincePaid >= 10 ? 'medium' : 'low',
            title: 'Recepción pendiente',
            message: `El pedido ${order.orderNumber} tiene productos pendientes de recibir (${daysSincePaid} días)`,
            orderId: order.id,
            orderNumber: order.orderNumber,
            date: paidDate,
            action: `/admin/pedidos-compra/${order.id}`
          })
        }
      }
    }
    
    // Ordenar por urgencia (high, medium, low) y luego por fecha
    const urgencyOrder = { high: 0, medium: 1, low: 2 }
    alerts.sort((a, b) => {
      const urgencyDiff = urgencyOrder[a.urgency] - urgencyOrder[b.urgency]
      if (urgencyDiff !== 0) return urgencyDiff
      return a.date - b.date
    })
    
    return alerts
  } catch (error) {
    console.error('Error obteniendo alertas de pedidos de compra:', error)
    return []
  }
}

export default {
  getAlertsSettings,
  updateAlertsSettings,
  getPurchaseOrderAlerts
}

